import React from 'react';
import { getTextColor } from '../../style';
import styled from '@emotion/styled';
import PriceInput from '../pages/Offers/PriceInput';
import colors from '../../theme/colors';
import { isDarkMode } from '../../utils/helpers';
import { CustomStyledProps } from 'style/style';
import { FONT_FAMILIES } from 'theme/material/constants';

const OfferAmountContainer = styled('div')({
  width: '100%',
  marginTop: 32,
});

const OfferAmountLabel = styled('label')({
  width: '100%',
  fontSize: 16,
  fontWeight: 600,
  lineHeight: '16px',
  letterSpacing: '-0.32px',
  color: '#595959',
});

const RedRequired = styled('span')(() => ({
  ...getTextColor().RED,
}));

const PriceContainer = styled('div')(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    marginTop: 10,
    marginBottom: 10,
    textAlign: 'center',
    width: '100%',
    height: 55,
    borderRadius: 6,
    backgroundColor: isDarkMode() ? '#041A2A' : '#FFF',
    border: isDarkMode() ? '1px solid #6C6C6C' : '1px solid #DAE1E9',
    fontFamily: FONT_FAMILIES.secondary,
  }),
  ({ hasErrors }: CustomStyledProps) =>
    hasErrors && {
      border: '1px solid #cc5454',
    }
);

const setColor = {
  default: {
    color: 'grey',
  },
  brand: {
    color: '#3e79a5',
  },
  alert: { ...getTextColor().RED },
};
const CurrencyTip = styled('div')(
  {
    display: 'inline-block',
    position: 'relative',
    paddingLeft: 10,
    fontFamily: FONT_FAMILIES.secondary,
  },
  ({ offerPrice }: CustomStyledProps) => ({
    ...setColor[offerPrice],
    ':before': {
      position: 'absolute',
      top: 14,
      right: '85%',
      fontSize: 16,
    },
  })
);

const CurrencyLabelSpan = styled('span')(() => ({
  fontSize: 42,
  fontWeight: 600,
  color: isDarkMode() ? colors['dark'].blue.secondary : colors['light'].blue.tertiary,
  letterSpacing: '-1.68px',
  lineHeight: '42px',
  fontFamily: FONT_FAMILIES.secondary,
}));

const InitialInputProps = {
  style: {
    fontSize: 42,
    height: 55,
    lineHeight: '42px',
    fontWeight: 600,
    outline: 'none',
    border: 'none',
    marginLeft: -17,
    color: isDarkMode() ? colors['dark'].blue.secondary : colors['light'].blue.tertiary,
    letterSpacing: '-1.68px',
    fontFamily: 'system-ui, -apple-system, Arial, sans-serif',
  },
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const adjustStylePerDigit = number => {
  const numString = number.toString();
  let marginLeft = -17;
  let paddingLeft = 10;

  switch (numString.length) {
    case 3:
      marginLeft = -10;
      paddingLeft = 10;
      break;
    case 2:
      marginLeft = -22;
      paddingLeft = 20;
      break;
    case 1:
      marginLeft = -32;
      paddingLeft = 30;
      break;
    default:
      marginLeft = -32;
      paddingLeft = 10;
      break;
  }
  return {
    marginLeft,
    paddingLeft,
  };
};

interface Props {
  offerPrice: string;
  updateOfferPrice: (offerPrice: string | number, hasError: boolean) => void;
  hasError: boolean;
  defaultMinOffer: string;
  currencyPrefix: string;
  isMandatoryField: boolean;
}

const JoinOfferAmountInput: React.FC<Props> = ({
  offerPrice,
  updateOfferPrice,
  hasError,
  defaultMinOffer,
  currencyPrefix,
  isMandatoryField,
}) => {
  const minValue = defaultMinOffer;
  const maxValue = '500';

  const inputOnChange = e => {
    const inputValue = Number(e.target.value);
    const MaxLength = maxValue.toString().length;

    if (e.target.value.length <= MaxLength) {
      const isInvalid = Boolean(Number(inputValue) < Number(minValue) || Number(inputValue) > Number(maxValue));

      updateOfferPrice(inputValue, isInvalid);
    } else {
      e.preventDefault();
    }
  };

  const colorType = (price, hasErrors) => {
    switch (price) {
      case true:
        return 'default';
      case false:
        return !hasErrors ? 'brand' : 'alert';
      default:
        return;
    }
  };

  return (
    <OfferAmountContainer>
      <OfferAmountLabel>
        {`Opening First Date Offer (${currencyPrefix}${defaultMinOffer} - ${currencyPrefix}500)`}
        {isMandatoryField ? <RedRequired>*</RedRequired> : ''}
      </OfferAmountLabel>
      <PriceContainer
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        hasErrors={hasError}
      >
        <CurrencyTip
          offerPrice={colorType(offerPrice, hasError)}
          style={{
            paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
          }}
        >
          <CurrencyLabelSpan>{currencyPrefix}</CurrencyLabelSpan>
          <PriceInput
            data-test-id="dashboard-discovermode-offer-price-input"
            hasErrors={hasError}
            value={offerPrice}
            onChange={inputOnChange}
            validateTextFn={validateTextInput}
            inputProps={{
              style: {
                ...InitialInputProps.style,
                marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
              },
            }}
            minWidth={100}
          />
        </CurrencyTip>
      </PriceContainer>
    </OfferAmountContainer>
  );
};

export default JoinOfferAmountInput;
